import { DimensionSide, Element, Point, Polyline } from '@ritabygglov/core';
import {
  BathTubPlanIcon,
  ChairPlanIcon,
  FixtureIconProps,
  SinkPlanIcon,
  TableAndChairsPlanIcon,
  WaterClosetPlanIcon,
} from '../components/Icons';
import {
  LayoutBox,
  tableAndChairsLayout,
} from '../helpers/tableAndChairsLayout';
import { PresetType } from '../types';
import { createGroup } from './Actions/Group';
import { createPolyline } from './Actions/Polyline';

export interface PresetBuildInput {
  x: number;
  y: number;
  renderingOrder: number;
  userId: string;
  generateUuid: () => string;
}

export interface PresetDefinition {
  icon: React.FC<FixtureIconProps>;
  toolbarViewBoxWidth: number;
  toolbarViewBoxHeight: number;
  toolbarId: string;
  i18nKey: string;
  build: (input: PresetBuildInput) => { group: Element; elements: Element[] };
}

type PolylineFactory = (renderingOrder: number) => Polyline;

const point = (x: number, y: number, cornerRadius = 0): Point => ({
  x,
  y,
  dimensionSide: DimensionSide.Above,
  cornerRadius,
});

const rectPolyline =
  (
    { x, y, width, height }: LayoutBox,
    { userId, generateUuid }: PresetBuildInput,
    cornerRadius = 0,
    frontCornerRadius = cornerRadius
  ): PolylineFactory =>
  (renderingOrder) => {
    const points = [
      point(x, y, cornerRadius),
      point(x + width, y, cornerRadius),
      point(x + width, y + height, frontCornerRadius),
      point(x, y + height, frontCornerRadius),
      { ...point(x, y), connectingPointIndex: 0 },
    ];
    return createPolyline(
      points[0],
      renderingOrder,
      generateUuid(),
      userId,
      undefined,
      points
    );
  };

const linePolyline =
  (
    from: { x: number; y: number },
    to: { x: number; y: number },
    { userId, generateUuid }: PresetBuildInput
  ): PolylineFactory =>
  (renderingOrder) => {
    const points = [point(from.x, from.y), point(to.x, to.y)];
    return createPolyline(
      points[0],
      renderingOrder,
      generateUuid(),
      userId,
      undefined,
      points
    );
  };

const chairPolylines = (
  { x, y, width, height }: LayoutBox,
  mirroredY: boolean,
  input: PresetBuildInput
): PolylineFactory[] => {
  const backrestDepth = height * 0.11;
  const backrestY = mirroredY ? y + height - backrestDepth : y + backrestDepth;

  return [
    rectPolyline({ x, y, width, height }, input),
    linePolyline({ x, y: backrestY }, { x: x + width, y: backrestY }, input),
  ];
};

const groupPreset =
  (
    width: number,
    height: number,
    children: (input: PresetBuildInput) => PolylineFactory[]
  ) =>
  (input: PresetBuildInput) => {
    const { x, y, renderingOrder, userId, generateUuid } = input;

    const elements = children(input).map((create, i) =>
      create(renderingOrder + i)
    );

    const group = createGroup(
      x,
      y,
      width,
      height,
      elements.map((child) => child.id),
      renderingOrder + elements.length,
      generateUuid(),
      userId
    );

    return { group, elements: [...elements, group] };
  };

const WATER_CLOSET_WIDTH = 16;
const WATER_CLOSET_HEIGHT = 27;

const buildWaterCloset = groupPreset(
  WATER_CLOSET_WIDTH,
  WATER_CLOSET_HEIGHT,
  (input) => {
    const { x, y } = input;
    const tankY = y + WATER_CLOSET_HEIGHT * 0.29;

    return [
      rectPolyline(
        { x, y, width: WATER_CLOSET_WIDTH, height: WATER_CLOSET_HEIGHT },
        input,
        0,
        WATER_CLOSET_WIDTH / 2
      ),
      linePolyline(
        { x, y: tankY },
        { x: x + WATER_CLOSET_WIDTH, y: tankY },
        input
      ),
    ];
  }
);

const SINK_WIDTH = 24;
const SINK_HEIGHT = 18;

const buildSink = groupPreset(SINK_WIDTH, SINK_HEIGHT, (input) => {
  const { x, y } = input;
  const rimWidth = Math.min(SINK_WIDTH, SINK_HEIGHT) * 0.12;
  const basinWidth = SINK_WIDTH - 2 * rimWidth;
  const basinHeight = SINK_HEIGHT - 2 * rimWidth;

  return [
    rectPolyline(
      { x, y, width: SINK_WIDTH, height: SINK_HEIGHT },
      input,
      0,
      Math.min(SINK_WIDTH, SINK_HEIGHT) / 2
    ),
    rectPolyline(
      {
        x: x + rimWidth,
        y: y + rimWidth,
        width: basinWidth,
        height: basinHeight,
      },
      input,
      rimWidth,
      Math.min(basinWidth, basinHeight) / 2
    ),
  ];
});

const BATH_TUB_WIDTH = 64;
const BATH_TUB_HEIGHT = 27;

const buildBathTub = groupPreset(BATH_TUB_WIDTH, BATH_TUB_HEIGHT, (input) => {
  const { x, y } = input;
  const rimWidth = Math.min(BATH_TUB_WIDTH, BATH_TUB_HEIGHT) * 0.12;
  const basinCornerRadius = rimWidth * 2;

  return [
    rectPolyline(
      { x, y, width: BATH_TUB_WIDTH, height: BATH_TUB_HEIGHT },
      input
    ),
    rectPolyline(
      {
        x: x + rimWidth,
        y: y + rimWidth,
        width: BATH_TUB_WIDTH - 2 * rimWidth,
        height: BATH_TUB_HEIGHT - 2 * rimWidth,
      },
      input,
      basinCornerRadius
    ),
  ];
});

const CHAIR_SIZE = 17;

const buildChair = groupPreset(CHAIR_SIZE, CHAIR_SIZE, (input) =>
  chairPolylines(
    { x: input.x, y: input.y, width: CHAIR_SIZE, height: CHAIR_SIZE },
    false,
    input
  )
);

const TABLE_AND_CHAIRS_WIDTH = 46;
const TABLE_AND_CHAIRS_HEIGHT = 69;

const buildTableAndChairs = groupPreset(
  TABLE_AND_CHAIRS_WIDTH,
  TABLE_AND_CHAIRS_HEIGHT,
  (input) => {
    const { x, y } = input;
    const { table, chairs } = tableAndChairsLayout(
      TABLE_AND_CHAIRS_WIDTH,
      TABLE_AND_CHAIRS_HEIGHT
    );

    return [
      rectPolyline({ ...table, x: x + table.x, y: y + table.y }, input),
      ...chairs.flatMap(({ mirroredY, ...chair }) =>
        chairPolylines(
          { ...chair, x: x + chair.x, y: y + chair.y },
          mirroredY,
          input
        )
      ),
    ];
  }
);

export const presetRegistry: Record<PresetType, PresetDefinition> = {
  [PresetType.WATER_CLOSET_PLAN]: {
    icon: WaterClosetPlanIcon,
    toolbarViewBoxWidth: 160,
    toolbarViewBoxHeight: 270,
    toolbarId: 'toolbar-water-closet-plan',
    i18nKey: 'toolbar.presetWaterClosetPlan',
    build: buildWaterCloset,
  },
  [PresetType.SINK_PLAN]: {
    icon: SinkPlanIcon,
    toolbarViewBoxWidth: 240,
    toolbarViewBoxHeight: 180,
    toolbarId: 'toolbar-sink-plan',
    i18nKey: 'toolbar.presetSinkPlan',
    build: buildSink,
  },
  [PresetType.BATH_TUB_PLAN]: {
    icon: BathTubPlanIcon,
    toolbarViewBoxWidth: 320,
    toolbarViewBoxHeight: 135,
    toolbarId: 'toolbar-bath-tub-plan',
    i18nKey: 'toolbar.presetBathTubPlan',
    build: buildBathTub,
  },
  [PresetType.CHAIR_PLAN]: {
    icon: ChairPlanIcon,
    toolbarViewBoxWidth: 170,
    toolbarViewBoxHeight: 170,
    toolbarId: 'toolbar-chair-plan',
    i18nKey: 'toolbar.presetChairPlan',
    build: buildChair,
  },
  [PresetType.TABLE_AND_CHAIRS_PLAN]: {
    icon: TableAndChairsPlanIcon,
    toolbarViewBoxWidth: 460,
    toolbarViewBoxHeight: 690,
    toolbarId: 'toolbar-table-and-chairs-plan',
    i18nKey: 'toolbar.presetTableAndChairsPlan',
    build: buildTableAndChairs,
  },
};
